import { X } from 'lucide-react';
import React from 'react';

import { env } from '@/config/env';
import { useProfilesData } from '@/features/user';
import {
  NotificationEmptyState,
  useNotificationsData,
} from '@/features/user-notifications';
import {
  hasSeenNotifications,
  useNotificationsDrawerStore,
} from '@/stores/notifications';
import { useSchedulePageStore } from '@/stores/schedule-page';
import { MenuTrigger } from '@/ui/menu';
import { cn } from '@/utils';

import { Notification as NotificationIcon } from '../icons';

export interface NotificationUIProps {
  isFooter?: boolean;
}

export const NotificationUI = ({ isFooter = false }: NotificationUIProps) => {
  const { shouldShowOnboarding } = useProfilesData();
  // Get all notification data from single comprehensive hook
  const {
    hasNotifications,
    notificationCards,
    enabledNotifications,
    hasError,
  } = useNotificationsData();

  const { isOpen: isScheduleDrawerOpen, setIsOpen: setIsScheduleDrawerOpen } =
    useSchedulePageStore();

  // Get notification store state and actions
  const seenNotificationIds = useNotificationsDrawerStore(
    state => state.seenNotificationIds
  );
  const markNotificationsAsSeen = useNotificationsDrawerStore(
    state => state.markNotificationsAsSeen
  );

  // Local UI state for mobile drawer and desktop menu
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = React.useState(false);
  const [isDesktopMenuOpen, setIsDesktopMenuOpen] = React.useState(false);

  // Get current notification IDs
  const currentNotificationIds = React.useMemo(
    () => enabledNotifications.map(n => n.id),
    [enabledNotifications]
  );

  // Show red dot only if there are unread notifications
  const showRedDot =
    hasNotifications &&
    !hasSeenNotifications(seenNotificationIds, currentNotificationIds);

  const handleMobileDrawerToggle = React.useCallback(() => {
    // Close schedule drawer when opening notifications
    if (!isMobileDrawerOpen && isScheduleDrawerOpen) {
      setIsScheduleDrawerOpen?.(false);
    }

    // Mark notifications as seen when opening
    if (!isMobileDrawerOpen && hasNotifications) {
      markNotificationsAsSeen(currentNotificationIds);
    }

    setIsMobileDrawerOpen(prev => !prev);
  }, [
    isMobileDrawerOpen,
    isScheduleDrawerOpen,
    setIsScheduleDrawerOpen,
    hasNotifications,
    markNotificationsAsSeen,
    currentNotificationIds,
  ]);

  const handleDesktopMenuOpenChange = React.useCallback(
    (open: boolean) => {
      setIsDesktopMenuOpen(open);

      // Mark notifications as seen when opening menu
      if (open && hasNotifications) {
        markNotificationsAsSeen(currentNotificationIds);
      }
    },
    [hasNotifications, markNotificationsAsSeen, currentNotificationIds]
  );

  const handleCloseMobileDrawer = React.useCallback(() => {
    setIsMobileDrawerOpen(false);
  }, []);

  // Hide notifications for offline mode and onboarding
  if (env.IS_OFFLINE || shouldShowOnboarding) {
    return null;
  }

  // Helper function to render notification content
  const renderNotificationContent = () => {
    if (hasError) {
      return (
        <div className="px-4 py-3 text-sm text-red-600">
          Failed to load notifications. Please refresh.
        </div>
      );
    }
    if (hasNotifications) {
      return notificationCards;
    }
    return <NotificationEmptyState />;
  };

  // Helper function to render mobile notification content
  const renderMobileNotificationContent = () => {
    if (hasError) {
      return (
        <div className="flex items-center justify-center min-h-[300px]">
          <div className="px-4 py-3 text-sm text-red-600 text-center">
            Failed to load notifications. Please refresh.
          </div>
        </div>
      );
    }
    if (hasNotifications) {
      return <div className="space-y-3 pt-4">{notificationCards}</div>;
    }
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <NotificationEmptyState />
      </div>
    );
  };

  // Desktop dropdown menu content
  const desktopMenu = (
    <div
      className={cn(
        'bg-magical-moonlight-s rounded-2xl shadow-2xl',
        'w-[420px] max-h-[70vh] overflow-y-auto',
        'hidden lg:block' // Desktop only (>= 1024px)
      )}
    >
      {/* Header */}
      <div className="sticky top-0 bg-magical-moonlight-s border-b border-magical-moonlight-d1/20 px-4 py-3 flex items-center justify-between rounded-t-2xl">
        <h2 className="text-base font-semibold">Notifications</h2>
      </div>

      {/* Content */}
      <div className="p-3 space-y-3">{renderNotificationContent()}</div>
    </div>
  );

  return (
    <>
      {/* Desktop: MenuTrigger with dropdown (hidden on mobile and tablet) */}
      <MenuTrigger
        menu={desktopMenu}
        align="center"
        side={isFooter ? 'top' : 'bottom'}
        menuClassName={isFooter ? 'mb-6' : 'mt-6'}
        className="hidden lg:flex items-center h-full"
        onOpenChange={handleDesktopMenuOpenChange}
      >
        <div className="relative h-full flex items-center justify-center">
          <button
            className="relative focus:outline-none flex items-center justify-center"
            aria-label={showRedDot ? 'Notifications (unread)' : 'Notifications'}
            aria-live="polite"
            aria-atomic="true"
          >
            {/* Rounded container - white background when open (desktop) */}
            <div
              className={cn(
                'relative rounded-2xl p-3 transition-colors',
                isDesktopMenuOpen
                  ? 'bg-white'
                  : 'bg-magical-moonlight-d1/10 hover:bg-magical-moonlight-d1/15'
              )}
            >
              <NotificationIcon className="w-6 h-6 text-magical-moonlight-d4" />
              {/* Red dot for unread notifications */}
              {showRedDot ? (
                <div className="absolute top-1 right-1 w-2.5 h-2.5 bg-red-500 rounded-full border border-white" />
              ) : null}
            </div>
          </button>

          {/* Orange indicator below icon when active - following navigation pattern */}
          {isDesktopMenuOpen ? (
            <div className="absolute -bottom-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="40"
                height="7"
                viewBox="0 0 40 7"
                fill="none"
              >
                <path
                  d="M11.458 2.4168C16.9415 0.223414 23.0585 0.223413 28.542 2.41679L40 7H0L11.458 2.4168Z"
                  fill="#F15D22"
                />
              </svg>
            </div>
          ) : null}
        </div>
      </MenuTrigger>

      {/* Mobile + Tablet: Trigger button (visible on mobile and tablet only) */}
      <div className="relative lg:hidden">
        <button
          onClick={handleMobileDrawerToggle}
          className="relative focus:outline-none flex items-center justify-center"
          aria-label={showRedDot ? 'Notifications (unread)' : 'Notifications'}
          aria-live="polite"
          aria-atomic="true"
        >
          {/* Rounded container - orange background when open (mobile) */}
          <div
            className={cn(
              'relative rounded-2xl p-3 transition-colors',
              isMobileDrawerOpen
                ? 'bg-bhanzu-orange'
                : 'bg-magical-moonlight-d2 hover:bg-magical-moonlight-d1'
            )}
          >
            <NotificationIcon className="w-6 h-6 text-white" />
            {/* Red dot for unread notifications */}
            {showRedDot ? (
              <div className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full border border-magical-moonlight-d2" />
            ) : null}
          </div>
        </button>
      </div>

      {/* Mobile + Tablet: Full-screen view (positioned between header and footer) */}
      {isMobileDrawerOpen ? (
        <div
          className="fixed inset-0 top-36 bottom-16 left-0 right-0 z-(--z-modal) bg-white flex flex-col overflow-hidden lg:hidden"
          role="dialog"
          aria-modal="true"
          aria-labelledby="mobile-notifications-title"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-6 border-b border-gray-200 flex-shrink-0 relative z-50">
            <h1
              id="mobile-notifications-title"
              className="text-2xl font-bold text-magical-moonlight-d4 capitalize"
            >
              Notifications
            </h1>
            <button
              type="button"
              onClick={handleCloseMobileDrawer}
              className="p-2 rounded-md hover:bg-gray-100 active:bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300 relative z-50"
              aria-label="Close notifications"
            >
              <X className="w-6 h-6 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-3">
            {renderMobileNotificationContent()}
          </div>
        </div>
      ) : null}
    </>
  );
};
